import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TipTapLink from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import BookImageNode, {
  insertBookImageAtPosition,
  insertBookImagesAtPosition,
} from './BookImageNode';
import BookTableNode from './BookTableNode';
import TableEditor from './TableEditor';
import BookIndent, { applyBookIndent } from './BookIndent';
import { convertLegacyTagsToHtml } from './legacyContent';
import {
  BookEquationBlock,
  BookEquationInline,
} from './BookEquation';
import EquationPanel from './EquationPanel';
import LinkPanel from './LinkPanel';
import VisualImageListEditor from './VisualImageListEditor';
import {
  BookSubscript,
  BookSuperscript,
  toggleBookScript,
} from './BookScriptMarks';
import {
  IMAGE_LANGUAGES,
  createImageSlot,
  getGalleryImageSlots,
  normalizeImageSlots,
  resolveActiveImage,
} from './contentLayout';
import { createProposalId } from './ImageProposalEditor';
import { ensureStableTableIds } from './bookTableAnchor';
import './ContentLayout.css';
import 'katex/dist/katex.min.css';

import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Palette, 
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Info,
  AlertTriangle,
  Bell,
  Lightbulb,
  ChevronDown,
  Move,
  ImagePlus,
  LoaderCircle,
  IndentDecrease,
  IndentIncrease,
  Sigma,
  Table2,
  Link as LinkIcon,
} from 'lucide-react';

const CustomTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style') || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      type: {
        default: 'note',
        parseHTML: (element) => element.getAttribute('data-type') || 'note',
        renderHTML: (attributes) => ({ 'data-type': attributes.type || 'note' }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-callout]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const type = HTMLAttributes?.['data-type'] || HTMLAttributes?.type || 'note';
    const base = 'callout my-3 rounded-lg border px-4 py-3 text-lg font-semibold text-white [&_p]:text-white [&_li]:text-white [&_strong]:text-white [&_em]:text-white [&_u]:text-white';
    const variants = {
      note: 'border-blue-700 bg-blue-600',
      warning: 'border-red-700 bg-red-600',
      reminder: 'border-amber-700 bg-amber-600',
      tip: 'border-green-700 bg-green-600',
    };
    const variant = variants[type] || variants.note;

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-callout': 'true',
        class: `${base} ${variant}`,
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setCallout:
        (type = 'note') =>
        ({ chain }) => {
          return chain().focus().wrapIn(this.name, { type }).run();
        },
      toggleCallout:
        (type = 'note') =>
        ({ editor, chain }) => {
          if (editor.isActive(this.name)) {
            return chain().focus().lift(this.name).run();
          }
          return chain().focus().wrapIn(this.name, { type }).run();
        },
      unsetCallout:
        () =>
        ({ chain }) => {
          return chain().focus().lift(this.name).run();
        },
    };
  },
});

const createImageId = () => {
  const randomPart =
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `img_${String(randomPart)
    .replace(/[^A-Za-z0-9-]/g, '')
    .slice(0, 64)}`;
};

const EMPTY_IMAGES = Object.freeze([]);

const RichTextEditor = ({ 
  content = '', 
  onChange, 
  placeholder = 'Commencez à écrire...',
  editable = true,
  className = '',
  images = EMPTY_IMAGES,
  onImagesChange,
  onUploadImage,
  contentLanguage = 'fr',
  canManageImages = true,
  canManageImageStructure = canManageImages,
  showImageLibrary = false,
  tables = [],
  onTablesChange,
  onEditTable,
  ariaLabel = 'Éditeur de texte enrichi',
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showFontSizePicker, setShowFontSizePicker] = useState(false);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [showTablePanel, setShowTablePanel] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [editingTableId, setEditingTableId] = useState('');
  const [showEquationPanel, setShowEquationPanel] = useState(false);
  const [activeEquation, setActiveEquation] = useState(null);
  const [linkPanel, setLinkPanel] = useState(null);
  const [existingImageIndex, setExistingImageIndex] = useState('');
  const [imageError, setImageError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [imageForm, setImageForm] = useState({
    captions: { fr: '', en: '', pt: '' },
    activeLanguages: [...IMAGE_LANGUAGES],
    align: 'center',
    widthUnit: 'percent',
    widthValue: 60,
    wrap: 'none',
  });
  const [selectionRange, setSelectionRange] = useState(null);
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const normalizedImages = useMemo(
    () => normalizeImageSlots(images),
    [images]
  );
  const imagesRef = useRef(normalizedImages);
  const onImagesChangeRef = useRef(onImagesChange);
  const onUploadImageRef = useRef(onUploadImage);
  const contentLanguageRef = useRef(contentLanguage);
  const canManageImagesRef = useRef(canManageImages);
  const canManageImageStructureRef = useRef(canManageImageStructure);
  const tablesRef = useRef(tables);
  const onTablesChangeRef = useRef(onTablesChange);
  const onEditTableRef = useRef(onEditTable);
  const previousTableIdsRef = useRef(null);
  const pendingRemovedTableIdsRef = useRef(new Set());
  const previousImageIdsRef = useRef(null);
  const pendingRemovedImageIdsRef = useRef(new Set());
  const savedCursorPositionRef = useRef(null);
  const savedEquationSelectionRef = useRef(null);
  const savedLinkSelectionRef = useRef(null);
  imagesRef.current = normalizedImages;
  onImagesChangeRef.current = onImagesChange;
  onUploadImageRef.current = onUploadImage;
  contentLanguageRef.current = contentLanguage;
  canManageImagesRef.current = canManageImages;
  canManageImageStructureRef.current = canManageImageStructure;
  onTablesChangeRef.current = onTablesChange;
  onEditTableRef.current = onEditTable;
  const tablesSignature = useMemo(() => JSON.stringify(tables || []), [tables]);
  const stableTables = useMemo(
    () => ensureStableTableIds(JSON.parse(tablesSignature)),
    [tablesSignature]
  );
  tablesRef.current = stableTables;
  const galleryChoices = useMemo(
    () =>
      getGalleryImageSlots(normalizedImages).map((slot) => ({
        slot,
        image:
          resolveActiveImage(slot, contentLanguage) ||
          (slot.proposals[0]
            ? {
                id: slot.id,
                url: slot.proposals[0].url,
                caption:
                  slot.proposals[0].captions?.[contentLanguage] || '',
              }
            : null),
        index: normalizedImages.findIndex((image) => image.id === slot.id),
      })),
    [contentLanguage, normalizedImages]
  );
  const imageIds = useMemo(
    () =>
      new Set(
        normalizedImages
          .map((image) =>
            image && typeof image === 'object' ? image.id : null
          )
          .filter(Boolean)
      ),
    [normalizedImages]
  );

  useEffect(() => {
    if (
      onTablesChangeRef.current &&
      JSON.stringify(stableTables) !== tablesSignature
    ) {
      onTablesChangeRef.current(stableTables);
    }
  }, [stableTables, tablesSignature]);

  // Palette de couleurs prédéfinies
  const colors = [
    '#000000', '#374151', '#FF0000', '#00FF00', '#0000FF', 
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500',
    '#800080', '#FFC0CB', '#A52A2A', '#808080'
  ];

  // Polices disponibles
  const fonts = [
    { name: 'Arial', value: 'Arial, sans-serif' },
    { name: 'Times New Roman', value: 'Times New Roman, serif' },
    { name: 'Calibri', value: 'Calibri, sans-serif' },
    { name: 'Verdana', value: 'Verdana, sans-serif' },
    { name: 'Georgia', value: 'Georgia, serif' },
    { name: 'Courier New', value: "'Courier New', Courier, monospace" },
    { name: 'Impact', value: 'Impact, Haettenschweiler, Arial Black, sans-serif' },
    { name: 'Comic Sans MS', value: "'Comic Sans MS', 'Comic Sans', cursive" }
  ];

  const fontSizes = [12, 14, 16, 18, 20, 24, 28, 32];

  const parseInlineStyle = useCallback((styleString) => {
    const result = {};
    if (!styleString || typeof styleString !== 'string') return result;
    styleString
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        const idx = part.indexOf(':');
        if (idx === -1) return;
        const key = part.slice(0, idx).trim().toLowerCase();
        const value = part.slice(idx + 1).trim();
        if (key) result[key] = value;
      });
    return result;
  }, []);

  const stringifyInlineStyle = useCallback((styleObj) => {
    if (!styleObj || typeof styleObj !== 'object') return '';
    return Object.entries(styleObj)
      .filter(([k, v]) => k && v)
      .map(([k, v]) => `${k}: ${v}`)
      .join('; ');
  }, []);

  const applyTextStylePatch = useCallback((patch) => {
    const editor = editorInstanceRef.current;
    if (!editor) return;
    const currentAttrs = editor.getAttributes('textStyle') || {};
    const currentStyle = currentAttrs.style || '';
    const styleObj = parseInlineStyle(currentStyle);

    Object.entries(patch || {}).forEach(([k, v]) => {
      if (!k) return;
      if (v === null || v === undefined || v === '') {
        delete styleObj[k.toLowerCase()];
      } else {
        styleObj[k.toLowerCase()] = v;
      }
    });
    const nextStyle = stringifyInlineStyle(styleObj);
    editor.chain().focus().setMark('textStyle', { ...currentAttrs, style: nextStyle }).run();
  }, [parseInlineStyle, stringifyInlineStyle]);

  const openExistingEquation = useCallback((equation) => {
    setActiveEquation(equation);
    setShowEquationPanel(true);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      BookIndent,
      Underline,
      BookSubscript,
      BookSuperscript,
      TipTapLink.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        protocols: ['http', 'https', 'mailto'],
        HTMLAttributes: {
          target: null,
          rel: null,
          class: null,
        },
      }),
      CustomTextStyle,
      FontFamily,
      Callout,
      BookImageNode.configure({
        resolveImageSlot: (imageId) =>
          imagesRef.current.find((slot) => slot?.id === imageId),
        onChangeImageSlot: (nextSlot) => {
          const nextImages = imagesRef.current.map((slot) =>
            slot?.id === nextSlot.id ? nextSlot : slot
          );
          imagesRef.current = nextImages;
          onImagesChangeRef.current?.(nextImages);
        },
        onReturnToGallery: (imageId) => {
          const nextImages = imagesRef.current.map((slot) =>
            slot?.id === imageId
              ? { ...slot, placement: { type: 'gallery' } }
              : slot
          );
          imagesRef.current = nextImages;
          onImagesChangeRef.current?.(nextImages);
        },
        onDeleteImageSlot: (imageId) => {
          const nextImages = imagesRef.current.filter(
            (slot) => slot?.id !== imageId
          );
          imagesRef.current = nextImages;
          onImagesChangeRef.current?.(nextImages);
        },
        onUploadImage: async (file) => {
          if (!onUploadImageRef.current) {
            throw new Error('Le téléversement d’image est indisponible.');
          }
          const uploadResult = await onUploadImageRef.current(file);
          const url =
            (typeof uploadResult === 'string' && uploadResult) ||
            uploadResult?.url ||
            uploadResult?.data?.url;
          if (!url) throw new Error("L’upload n’a pas retourné d’URL.");
          return { ...uploadResult, url };
        },
        contentLanguage: () => contentLanguageRef.current,
        canManageImages: () => canManageImagesRef.current,
        canManageImageStructure: () =>
          canManageImageStructureRef.current,
      }),
      BookTableNode.configure({
        resolveTable: (tableId) =>
          tablesRef.current.find((table) => table?.id === tableId),
        onEditTable: (tableId) => {
          setEditingTableId(tableId);
          onEditTableRef.current?.(tableId);
        },
        onMoveTable: (tableId) => {
          const currentEditor = editorInstanceRef.current;
          if (currentEditor) {
            savedCursorPositionRef.current = currentEditor.state.selection.from;
          }
          setSelectedTableId(tableId);
          setShowTablePanel(true);
        },
      }),
      BookEquationInline.configure({
        onEditEquation: openExistingEquation,
      }),
      BookEquationBlock.configure({
        onEditEquation: openExistingEquation,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: convertLegacyTagsToHtml(content),
    editable,
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) {
        onChange(html);
      }
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;
      savedCursorPositionRef.current = from;
      if (from !== to) {
        setSelectionRange({ from, to });
      } else {
        setSelectionRange(null);
      }
    },
  });

  editorInstanceRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(
      editor.state.tr.setMeta('bookImageSlotsChanged', true)
    );
  }, [
    editor,
    normalizedImages,
    contentLanguage,
    canManageImages,
    canManageImageStructure,
  ]);

  useEffect(() => {
    const nextTableIds = new Set(
      stableTables.map((table) => table?.id).filter(Boolean)
    );
    const previousTableIds = previousTableIdsRef.current;
    if (previousTableIds) {
      previousTableIds.forEach((tableId) => {
        if (!nextTableIds.has(tableId)) {
          pendingRemovedTableIdsRef.current.add(tableId);
        }
      });
    }
    nextTableIds.forEach((tableId) =>
      pendingRemovedTableIdsRef.current.delete(tableId)
    );
    previousTableIdsRef.current = nextTableIds;

    if (!editor) return;
    const removedTableIds = Array.from(pendingRemovedTableIdsRef.current);
    pendingRemovedTableIdsRef.current.clear();
    removedTableIds.forEach((tableId) => {
      editor.commands.removeBookTable({ tableId });
    });
  }, [editor, stableTables]);

  useEffect(() => {
    const previousImageIds = previousImageIdsRef.current;
    if (previousImageIds) {
      previousImageIds.forEach((imageId) => {
        if (!imageIds.has(imageId)) {
          pendingRemovedImageIdsRef.current.add(imageId);
        }
      });
    }
    imageIds.forEach((imageId) =>
      pendingRemovedImageIdsRef.current.delete(imageId)
    );
    previousImageIdsRef.current = imageIds;

    if (!editor) return;
    const removedImageIds = Array.from(
      pendingRemovedImageIdsRef.current
    );
    pendingRemovedImageIdsRef.current.clear();
    removedImageIds.forEach((imageId) => {
      editor.commands.removeBookImage({ imageId });
    });
  }, [editor, imageIds]);

  const openImagePanel = useCallback(() => {
    if (!editor) return;
    savedCursorPositionRef.current = editor.state.selection.from;
    setImageError('');
    setImageFiles([]);
    setExistingImageIndex('');
    setShowImagePanel(true);
  }, [editor]);

  const updateImageAlignment = useCallback((align) => {
    setImageForm((current) => {
      if (align === 'full') {
        return {
          ...current,
          align,
          widthUnit: 'percent',
          widthValue: 100,
          wrap: 'none',
        };
      }
      if (align === 'center') {
        return { ...current, align, wrap: 'none' };
      }
      return { ...current, align };
    });
  }, []);

  const insertUploadedImage = useCallback(async () => {
    if (
      !editor ||
      imageFiles.length === 0 ||
      !onUploadImage ||
      !onImagesChange
    ) {
      setImageError('Choisissez au moins un fichier image.');
      return;
    }

    const widthValue = Number(imageForm.widthValue);
    const isValidWidth =
      Number.isFinite(widthValue) &&
      (imageForm.widthUnit === 'px'
        ? Number.isInteger(widthValue) &&
          widthValue >= 80 &&
          widthValue <= 2000
        : widthValue >= 10 && widthValue <= 100);
    if (!isValidWidth) {
      setImageError(
        imageForm.widthUnit === 'px'
          ? 'La largeur doit être un entier entre 80 et 2000 px.'
          : 'La largeur doit être comprise entre 10 et 100 %.'
      );
      return;
    }

    setUploadingImage(true);
    setImageUploadProgress({ current: 1, total: imageFiles.length });
    setImageError('');
    let insertionStarted = false;
    try {
      const metadataList = [];
      const nodeAttributes = [];
      for (const [fileIndex, file] of imageFiles.entries()) {
        setImageUploadProgress({
          current: fileIndex + 1,
          total: imageFiles.length,
        });
        const uploadResult = await onUploadImage(file);
        const url =
          (typeof uploadResult === 'string' && uploadResult) ||
          uploadResult?.url ||
          uploadResult?.data?.url;
        if (!url) throw new Error("L’upload n’a pas retourné d’URL.");

        const imageId = createImageId();
        const metadata = createImageSlot({
          id: imageId,
          proposalId: createProposalId(),
          url,
          captions: imageForm.captions,
          activeLanguages: imageForm.activeLanguages,
          placement: {
            type: 'inline',
            align: imageForm.align,
            width: { unit: imageForm.widthUnit, value: widthValue },
            wrap:
              ['center', 'full'].includes(imageForm.align)
                ? 'none'
                : imageForm.wrap,
          },
        });
        const preview =
          resolveActiveImage(metadata, contentLanguageRef.current) ||
          metadata.proposals[0];
        metadataList.push(metadata);
        nodeAttributes.push({
          imageId,
          previewUrl: preview?.url || url,
          previewCaption:
            preview?.caption ||
            preview?.captions?.[contentLanguageRef.current] ||
            '',
          previewAlign: imageForm.align,
          previewWrap: metadata.placement.wrap,
          previewWidthUnit: imageForm.widthUnit,
          previewWidthValue: widthValue,
        });
      }

      insertionStarted = true;
      const inserted = insertBookImagesAtPosition(
        editor,
        savedCursorPositionRef.current ?? editor.state.selection.from,
        nodeAttributes
      );
      if (!inserted) {
        throw new Error("Impossible d’insérer les images à cette position.");
      }
      const nextImages = [...imagesRef.current, ...metadataList];
      imagesRef.current = nextImages;
      onImagesChange(nextImages);
      setShowImagePanel(false);
      setImageFiles([]);
      setExistingImageIndex('');
      setImageForm({
        captions: { fr: '', en: '', pt: '' },
        activeLanguages: [...IMAGE_LANGUAGES],
        align: 'center',
        widthUnit: 'percent',
        widthValue: 60,
        wrap: 'none',
      });
    } catch (error) {
      setImageError(
        insertionStarted
          ? error?.message || "Impossible d’insérer cette image."
          : 'Le téléversement a échoué.'
      );
    } finally {
      setUploadingImage(false);
      setImageUploadProgress({ current: 0, total: 0 });
    }
  }, [
    editor,
    imageFiles,
    imageForm,
    onImagesChange,
    onUploadImage,
  ]);

  const insertExistingImage = useCallback(() => {
    if (!editor || !onImagesChange || existingImageIndex === '') {
      setImageError('Choisissez une image existante.');
      return;
    }
    const index = Number(existingImageIndex);
    const existing = imagesRef.current[index];
    if (
      !existing ||
      typeof existing !== 'object' ||
      !Array.isArray(existing.proposals) ||
      existing.proposals.length === 0
    ) {
      setImageError('Cette image existante est invalide.');
      return;
    }
    const widthValue = Number(imageForm.widthValue);
    const isValidWidth =
      Number.isFinite(widthValue) &&
      (imageForm.widthUnit === 'px'
        ? Number.isInteger(widthValue) &&
          widthValue >= 80 &&
          widthValue <= 2000
        : widthValue >= 10 && widthValue <= 100);
    if (!isValidWidth) {
      setImageError(
        imageForm.widthUnit === 'px'
          ? 'La largeur doit être un entier entre 80 et 2000 px.'
          : 'La largeur doit être comprise entre 10 et 100 %.'
      );
      return;
    }

    const imageId = existing.id;
    const metadata = {
      ...existing,
      placement: {
        type: 'inline',
        align: imageForm.align,
        width: { unit: imageForm.widthUnit, value: widthValue },
        wrap:
          ['center', 'full'].includes(imageForm.align)
            ? 'none'
            : imageForm.wrap,
      },
    };
    const preview =
      resolveActiveImage(metadata, contentLanguageRef.current) ||
      metadata.proposals[0];
    const inserted = insertBookImageAtPosition(
      editor,
      savedCursorPositionRef.current ?? editor.state.selection.from,
      {
        imageId,
        previewUrl: preview?.url || '',
        previewCaption:
          preview?.caption ||
          preview?.captions?.[contentLanguageRef.current] ||
          '',
        previewAlign: metadata.placement.align,
        previewWrap: metadata.placement.wrap,
        previewWidthUnit: metadata.placement.width.unit,
        previewWidthValue: metadata.placement.width.value,
      }
    );
    if (!inserted) {
      setImageError("Impossible d’insérer l’image à cette position.");
      return;
    }
    const nextImages = [...imagesRef.current];
    nextImages[index] = metadata;
    imagesRef.current = nextImages;
    onImagesChange(nextImages);
    setShowImagePanel(false);
    setExistingImageIndex('');
    setImageForm({
      captions: { fr: '', en: '', pt: '' },
      activeLanguages: [...IMAGE_LANGUAGES],
      align: 'center',
      widthUnit: 'percent',
      widthValue: 60,
      wrap: 'none',
    });
  }, [
    editor,
    existingImageIndex,
    imageForm,
    onImagesChange,
  ]);

  // Gérer le clic sur la barre d'outils
  const handleToolbarAction = useCallback((action, value = null) => {
    if (!editor) return;
    
    switch (action) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'underline':
        editor.chain().focus().toggleUnderline().run();
        break;
      case 'subscript':
        toggleBookScript(editor, 'subscript');
        break;
      case 'superscript':
        toggleBookScript(editor, 'superscript');
        break;
      case 'bulletList':
        editor.chain().focus().toggleBulletList().run();
        break;
      case 'orderedList':
        editor.chain().focus().toggleOrderedList().run();
        break;
      case 'align':
        if (value) {
          editor.chain().focus().setTextAlign(value).run();
        }
        break;
      case 'color':
        if (value) {
          applyTextStylePatch({ color: value });
          setShowColorPicker(false);
        }
        break;
      case 'font':
        if (value) {
          editor.chain().focus().setFontFamily(value).run();
          setShowFontPicker(false);
        }
        break;
      case 'fontSize':
        if (value) {
          applyTextStylePatch({ 'font-size': `${value}px` });
          setShowFontSizePicker(false);
        }
        break;
      case 'callout':
        if (value) {
          editor.chain().focus().toggleCallout(value).run();
        }
        break;
      case 'unsetCallout':
        editor.chain().focus().unsetCallout().run();
        break;
      default:
        break;
    }
  }, [editor, applyTextStylePatch]);

  const handleIndent = useCallback((direction) => {
    applyBookIndent(editor, direction);
  }, [editor]);

  const openTablePanel = useCallback(() => {
    if (!editor || !tablesRef.current.length) return;
    savedCursorPositionRef.current = editor.state.selection.from;
    setSelectedTableId((current) =>
      tablesRef.current.some((table) => table.id === current)
        ? current
        : tablesRef.current[0].id
    );
    setShowTablePanel(true);
  }, [editor]);

  const placeSelectedTable = useCallback(() => {
    if (!editor || !selectedTableId) return;
    if (!tablesRef.current.some((table) => table.id === selectedTableId)) {
      return;
    }
    const maxPosition = editor.state.doc.content.size;
    const savedPosition = savedCursorPositionRef.current;
    const safePosition = Math.max(
      0,
      Math.min(
        Number.isInteger(savedPosition)
          ? savedPosition
          : editor.state.selection.from,
        maxPosition
      )
    );
    editor
      .chain()
      .focus()
      .setTextSelection(safePosition)
      .placeBookTable({ tableId: selectedTableId })
      .run();
    setShowTablePanel(false);
  }, [editor, selectedTableId]);

  const cancelTablePlacement = useCallback(() => {
    setShowTablePanel(false);
    const savedPosition = savedCursorPositionRef.current;
    window.setTimeout(() => {
      if (!editor) return;
      if (Number.isInteger(savedPosition)) {
        const safePosition = Math.max(
          0,
          Math.min(savedPosition, editor.state.doc.content.size)
        );
        editor.commands.setTextSelection(safePosition);
      }
      editor.view.dom.focus();
    }, 0);
  }, [editor]);

  const openLibraryImagePanel = useCallback(
    (imageIndex) => {
      if (!editor || !Number.isInteger(imageIndex)) return;
      const selectedImage = imagesRef.current[imageIndex];
      if (
        !selectedImage ||
        typeof selectedImage !== 'object' ||
        !Array.isArray(selectedImage.proposals) ||
        selectedImage.proposals.length === 0 ||
        selectedImage.placement?.type === 'inline'
      ) {
        return;
      }
      savedCursorPositionRef.current = editor.state.selection.from;
      setImageError('');
      setImageFiles([]);
      setExistingImageIndex(String(imageIndex));
      setShowImagePanel(true);
    },
    [editor]
  );

  const changeVisualImages = useCallback((nextImages) => {
    const normalized = normalizeImageSlots(nextImages);
    imagesRef.current = normalized;
    onImagesChangeRef.current?.(normalized);
  }, []);

  const editingTable = stableTables.find(
    (table) => table?.id === editingTableId
  );

  useEffect(() => {
    if (editingTableId && !editingTable) setEditingTableId('');
  }, [editingTable, editingTableId]);

  const updateEditingTable = useCallback((nextTable) => {
    if (!editingTableId) return;
    const nextTables = tablesRef.current.map((table) =>
      table?.id === editingTableId
        ? { ...nextTable, id: editingTableId }
        : table
    );
    tablesRef.current = nextTables;
    onTablesChangeRef.current?.(nextTables);
  }, [editingTableId]);

  const openEquationPanel = useCallback(() => {
    const currentEditor = editorInstanceRef.current;
    if (!currentEditor) return;
    const { from, to } = currentEditor.state.selection;
    savedEquationSelectionRef.current = { from, to };
    setActiveEquation(null);
    setShowEquationPanel(true);
  }, []);

  const openLinkPanel = useCallback(() => {
    const currentEditor = editorInstanceRef.current;
    if (!currentEditor) return;
    const editing = currentEditor.isActive('link');
    if (editing) currentEditor.commands.extendMarkRange('link');
    const { from, to } = currentEditor.state.selection;
    const linkAttributes = editing
      ? currentEditor.getAttributes('link')
      : {};
    savedLinkSelectionRef.current = { from, to };
    setLinkPanel({
      initialText:
        from === to ? '' : currentEditor.state.doc.textBetween(from, to, ''),
      initialHref: linkAttributes.href || '',
      editing,
    });
  }, []);

  const openClickedLink = useCallback((event) => {
    const target = event.target;
    const anchor =
      target instanceof Element ? target.closest('a[href]') : null;
    const currentEditor = editorInstanceRef.current;
    if (!anchor || !currentEditor?.view.dom.contains(anchor)) return;
    event.preventDefault();
    const from = currentEditor.view.posAtDOM(anchor, 0);
    const text = anchor.textContent || '';
    savedLinkSelectionRef.current = { from, to: from + text.length };
    setLinkPanel({
      initialText: text,
      initialHref: anchor.getAttribute('href') || '',
      editing: true,
    });
  }, []);

  const confirmLink = useCallback(({ text, href, target, rel }) => {
    const currentEditor = editorInstanceRef.current;
    const saved = savedLinkSelectionRef.current;
    if (!currentEditor || !saved) return;
    currentEditor
      .chain()
      .insertContentAt(saved, {
        type: 'text',
        text,
        marks: [
          {
            type: 'link',
            attrs: { href, target: target || null, rel: rel || null },
          },
        ],
      })
      .focus(null, { scrollIntoView: false })
      .run();
    savedLinkSelectionRef.current = null;
    setLinkPanel(null);
  }, []);

  const removeLink = useCallback(() => {
    const currentEditor = editorInstanceRef.current;
    const saved = savedLinkSelectionRef.current;
    if (!currentEditor || !saved) return;
    currentEditor
      .chain()
      .setTextSelection(saved)
      .unsetLink()
      .focus(null, { scrollIntoView: false })
      .run();
    savedLinkSelectionRef.current = null;
    setLinkPanel(null);
  }, []);

  const cancelLink = useCallback(() => {
    const currentEditor = editorInstanceRef.current;
    const saved = savedLinkSelectionRef.current;
    setLinkPanel(null);
    window.setTimeout(() => {
      if (!currentEditor) return;
      if (saved) {
        currentEditor
          .chain()
          .setTextSelection(saved)
          .focus()
          .run();
      } else {
        currentEditor.view.dom.focus();
      }
    }, 0);
  }, []);

  const closeEquationPanel = useCallback(() => {
    setShowEquationPanel(false);
    setActiveEquation(null);
  }, []);

  const confirmEquation = useCallback(
    (equation) => {
      if (!editor) return;
      if (Number.isInteger(activeEquation?.pos)) {
        editor.commands.updateBookEquation({
          pos: activeEquation.pos,
          latex: equation.latex,
          display: equation.display,
        });
      } else {
        const selection = savedEquationSelectionRef.current;
        const chain = editor.chain().focus();
        if (selection) chain.setTextSelection(selection);
        chain.insertBookEquation(equation).run();
      }
      closeEquationPanel();
    },
    [activeEquation, closeEquationPanel, editor]
  );

  const deleteEquation = useCallback(() => {
    if (editor && Number.isInteger(activeEquation?.pos)) {
      editor.commands.deleteBookEquation({ pos: activeEquation.pos });
    }
    closeEquationPanel();
  }, [activeEquation, closeEquationPanel, editor]);

  const cancelEquation = useCallback(() => {
    const selection = savedEquationSelectionRef.current;
    closeEquationPanel();
    window.setTimeout(() => {
      if (!editor) return;
      if (!activeEquation && selection) {
        editor.commands.setTextSelection(selection);
      }
      editor.view.dom.focus();
    }, 0);
  }, [activeEquation, closeEquationPanel, editor]);

  const getCurrentTextAlign = useCallback(() => {
    if (!editor) return 'left';
    const paragraphAlign = editor.getAttributes('paragraph')?.textAlign;
    const headingAlign = editor.getAttributes('heading')?.textAlign;
    return paragraphAlign || headingAlign || 'left';
  }, [editor]);

  const getCurrentFontSize = useCallback(() => {
    if (!editor) return null;
    const currentStyle = editor.getAttributes('textStyle').style || '';
    const styleObj = parseInlineStyle(currentStyle);
    const size = styleObj['font-size'];
    if (!size) return null;
    const m = String(size).match(/(\d+(?:\.\d+)?)/);
    return m ? Number(m[1]) : null;
  }, [editor, parseInlineStyle]);

  // Vérifier si un bouton est actif
  const isActive = useCallback((action) => {
    if (!editor) return false;
    switch (action) {
      case 'bold':
        return editor.isActive('bold');
      case 'italic':
        return editor.isActive('italic');
      case 'underline':
        return editor.isActive('underline');
      case 'subscript':
        return editor.isActive('subscript');
      case 'superscript':
        return editor.isActive('superscript');
      case 'bulletList':
        return editor.isActive('bulletList');
      case 'orderedList':
        return editor.isActive('orderedList');
      case 'alignLeft':
        return getCurrentTextAlign() === 'left';
      case 'alignCenter':
        return getCurrentTextAlign() === 'center';
      case 'alignRight':
        return getCurrentTextAlign() === 'right';
      case 'alignJustify':
        return getCurrentTextAlign() === 'justify';
      case 'color':
        return editor.isActive('textStyle');
      case 'font':
        const currentStyle = editor.getAttributes('textStyle').style || '';
        return currentStyle.includes('font-family');
      case 'fontSize':
        const currentFontSize = getCurrentFontSize();
        return currentFontSize !== null;
      case 'calloutNote':
        return editor.isActive('callout', { type: 'note' });
      case 'calloutWarning':
        return editor.isActive('callout', { type: 'warning' });
      case 'calloutReminder':
        return editor.isActive('callout', { type: 'reminder' });
      case 'calloutTip':
        return editor.isActive('callout', { type: 'tip' });
      default:
        return false;
    }
  }, [editor, getCurrentTextAlign, getCurrentFontSize]);

  // Afficher la barre d'outils uniquement si du texte est sélectionné
  const shouldShowToolbar = selectionRange && editable;

  // Positionner la barre d'outils près de la sélection
  const getToolbarPosition = useCallback(() => {
    if (!editorRef.current || !selectionRange || !editor) return {};
    
    const { from } = selectionRange;
    let coords;
    try {
      coords = editor.view.coordsAtPos(from);
    } catch {
      return { position: 'absolute', top: 0, left: 0, zIndex: 1000 };
    }
    
    return {
      position: 'absolute',
      top: `${coords.top - 60}px`,
      left: `${coords.left}px`,
      zIndex: 1000
    };
  }, [editor, selectionRange]);

  // Forcer la détection de sélection avec un événement supplémentaire
  useEffect(() => {
    if (!editor) return;

    const handleSelectionChange = () => {
      const { from, to } = editor.state.selection;
      if (from !== to) {
        setSelectionRange({ from, to });
      } else {
        setSelectionRange(null);
      }
    };

    // Écouter les événements de sélection
    editor.on('selectionUpdate', handleSelectionChange);
    
    // Ajouter un écouteur pour la sélection de la souris
    const handleMouseUp = () => {
      setTimeout(handleSelectionChange, 10);
    };
    
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keyup', handleSelectionChange);
    
    return () => {
      editor.off('selectionUpdate', handleSelectionChange);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keyup', handleSelectionChange);
    };
  }, [editor]);

  // Fermer les sélecteurs quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est dans le color picker ou le font picker
      const isColorPicker = event.target.closest('[data-color-picker]');
      const isFontPicker = event.target.closest('[data-font-picker]');
      const isFontSizePicker = event.target.closest('[data-font-size-picker]');
      
      if (!isColorPicker && !isFontPicker && !isFontSizePicker && toolbarRef.current && !toolbarRef.current.contains(event.target)) {
        setShowColorPicker(false);
        setShowFontPicker(false);
        setShowFontSizePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!editor) {
    return <div className="animate-pulse bg-gray-200 h-24 rounded"></div>;
  }

  return (
    <div className={`relative ${className}`} ref={editorRef}>
      {/* Barre d'outils fixe (toujours visible) */}
      {editable && (
        <div
          role="toolbar"
          aria-label="Outils de mise en forme"
          className="mb-2 flex w-full min-w-0 flex-wrap items-center gap-1 rounded-lg border border-gray-300 bg-white p-2 shadow-md [&>button]:shrink-0 [&>div]:shrink-0"
        >
          {/* Gras */}
          <button
            onClick={() => handleToolbarAction('bold')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('bold') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Gras"
          >
            <Bold size={16} />
          </button>

          {/* Italique */}
          <button
            onClick={() => handleToolbarAction('italic')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('italic') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Italique"
          >
            <Italic size={16} />
          </button>

          {/* Souligné */}
          <button
            onClick={() => handleToolbarAction('underline')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('underline') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Souligné"
          >
            <UnderlineIcon size={16} />
          </button>

          <button
            type="button"
            onClick={() => handleToolbarAction('subscript')}
            className={`rounded px-2 py-1.5 text-sm font-semibold hover:bg-gray-100 ${
              isActive('subscript') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            aria-label="Indice X₂"
            aria-pressed={isActive('subscript')}
            title="Indice (texte en bas)"
          >
            X<sub>2</sub>
          </button>

          <button
            type="button"
            onClick={() => handleToolbarAction('superscript')}
            className={`rounded px-2 py-1.5 text-sm font-semibold hover:bg-gray-100 ${
              isActive('superscript') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            aria-label="Exposant X²"
            aria-pressed={isActive('superscript')}
            title="Exposant (texte en haut)"
          >
            X<sup>2</sup>
          </button>

          {/* Séparateur */}
          <div className="w-px h-6 bg-gray-300 mx-1" />

          <button
            type="button"
            onClick={openLinkPanel}
            className="inline-flex items-center gap-1 rounded px-2 py-2 text-sm text-gray-700 hover:bg-gray-100"
            aria-label="Lien"
            title="Ajouter ou modifier un lien"
          >
            <LinkIcon size={16} />
            <span>Lien</span>
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Listes */}
          <button
            onClick={() => handleToolbarAction('bulletList')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('bulletList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Liste à puces"
          >
            <List size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('orderedList')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('orderedList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Liste numérotée"
          >
            <ListOrdered size={16} />
          </button>

          <button
            type="button"
            onClick={() => handleIndent('decrease')}
            className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
            aria-label="Diminuer le retrait"
            title="Diminuer le retrait"
          >
            <IndentDecrease size={16} />
          </button>

          <button
            type="button"
            onClick={() => handleIndent('increase')}
            className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
            aria-label="Augmenter le retrait"
            title="Augmenter le retrait"
          >
            <IndentIncrease size={16} />
          </button>

          {/* Alignement */}
          <button
            onClick={() => handleToolbarAction('align', 'left')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignLeft') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Aligner à gauche"
          >
            <AlignLeft size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('align', 'center')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignCenter') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Centrer"
          >
            <AlignCenter size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('align', 'right')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignRight') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Aligner à droite"
          >
            <AlignRight size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('align', 'justify')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignJustify') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Justifier"
          >
            <AlignJustify size={16} />
          </button>

          {/* Séparateur */}
          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Couleur */}
          <div className="relative">
            <button
              onClick={() => {
                setShowColorPicker(!showColorPicker);
              }}
              className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
              title="Couleur du texte"
            >
              <Palette size={16} />
            </button>
            
            {showColorPicker && (
              <div data-color-picker className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl p-3 grid grid-cols-4 gap-2 z-50 min-w-[200px]">
                {colors.map((color) => (
                  <button
                    key={color}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToolbarAction('color', color);
                    }}
                    className="w-8 h-8 rounded-lg border-2 border-gray-300 hover:border-gray-400 hover:scale-110 transition-all cursor-pointer shadow-sm"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Police */}
          <div className="relative">
            <button
              onClick={() => setShowFontPicker(!showFontPicker)}
              className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600 flex items-center gap-1"
              title="Police"
            >
              <Type size={16} />
              <ChevronDown size={12} />
            </button>
            
            {showFontPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 min-w-[150px] z-50">
                {fonts.map((font) => (
                  <button
                    key={font.value}
                    onClick={() => handleToolbarAction('font', font.value)}
                    className="block w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Taille de police */}
          <div className="relative">
            <button
              onClick={() => setShowFontSizePicker(!showFontSizePicker)}
              className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600 flex items-center gap-1"
              title="Taille de police"
            >
              <span className="text-xs font-semibold">{getCurrentFontSize() || 16}px</span>
              <ChevronDown size={12} />
            </button>

            {showFontSizePicker && (
              <div data-font-size-picker className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 min-w-[120px] z-50">
                {fontSizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => handleToolbarAction('fontSize', size)}
                    className="block w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                    style={{ fontSize: `${size}px` }}
                  >
                    {size}px
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Callouts */}
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <button
            onClick={() => handleToolbarAction('callout', 'note')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('calloutNote') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Note"
          >
            <Info size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('callout', 'warning')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('calloutWarning') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Warning"
          >
            <AlertTriangle size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('callout', 'reminder')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('calloutReminder') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Reminder"
          >
            <Bell size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('callout', 'tip')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('calloutTip') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Tip"
          >
            <Lightbulb size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('unsetCallout')}
            className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
            title="Retirer le bloc"
          >
            <Move size={16} />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1" />
          <button
            type="button"
            onClick={openEquationPanel}
            className="inline-flex items-center gap-1 rounded px-2 py-2 text-sm text-gray-700 hover:bg-gray-100"
            aria-label="Équation"
            title="Ajouter une équation"
          >
            <Sigma size={16} />
            <span>Équation</span>
          </button>

          {canManageImages &&
            canManageImageStructure &&
            onUploadImage &&
            onImagesChange && (
            <>
              <div className="w-px h-6 bg-gray-300 mx-1" />
              <button
                type="button"
                onClick={openImagePanel}
                className="inline-flex items-center gap-1.5 p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
                aria-label="Ajouter une image au curseur"
                title="Insérer une image au curseur"
              >
                <ImagePlus size={16} />
                <span>Image au curseur</span>
              </button>
            </>
          )}

          {stableTables.length > 0 && (
            <>
              <div className="w-px h-6 bg-gray-300 mx-1" />
              <button
                type="button"
                onClick={openTablePanel}
                className="inline-flex items-center gap-1 rounded px-2 py-2 text-sm text-gray-700 hover:bg-gray-100"
                aria-label="Placer un tableau au curseur"
                title="Placer un tableau au curseur"
              >
                <Table2 size={16} />
                <span>Tableau au curseur</span>
              </button>
            </>
          )}
        </div>
      )}

      {showEquationPanel && (
        <EquationPanel
          open
          initialEquation={activeEquation}
          onConfirm={confirmEquation}
          onDelete={deleteEquation}
          onCancel={cancelEquation}
        />
      )}

      {linkPanel && (
        <LinkPanel
          open
          initialText={linkPanel.initialText}
          initialHref={linkPanel.initialHref}
          editing={linkPanel.editing}
          onConfirm={confirmLink}
          onRemove={removeLink}
          onCancel={cancelLink}
        />
      )}

      {showImagePanel && (
        <div className="mb-3 grid gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm font-semibold text-blue-900">
            L’image sera insérée à la position mémorisée du curseur.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {galleryChoices.length > 0 && (
              <label className="grid gap-1 text-sm">
                <span>Image existante</span>
                <select
                  aria-label="Image existante"
                  className="rounded border border-gray-300 px-2 py-1"
                  value={existingImageIndex}
                  onChange={(event) => {
                    const nextIndex = event.target.value;
                    setExistingImageIndex(nextIndex);
                    const selectedImage =
                      nextIndex === ''
                        ? null
                        : imagesRef.current[Number(nextIndex)];
                    if (selectedImage) {
                      setImageFiles([]);
                    }
                  }}
                >
                  <option value="">Téléverser une nouvelle image</option>
                  {galleryChoices.map(({ slot, image, index }) => (
                    <option value={String(index)} key={slot.id}>
                      {image?.caption || image?.url || slot.id}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="grid gap-1 text-sm">
              <span>Fichier</span>
              <input
                aria-label="Fichier image"
                type="file"
                accept="image/*"
                multiple
                disabled={existingImageIndex !== ''}
                onChange={(event) =>
                  setImageFiles(Array.from(event.target.files || []))
                }
              />
            </label>

            {IMAGE_LANGUAGES.map((language) => (
              <label key={language} className="grid gap-1 text-sm">
                <span>Légende {language.toUpperCase()}</span>
                <input
                  aria-label={`Légende ${language.toUpperCase()} de la nouvelle image`}
                  className="rounded border border-gray-300 px-2 py-1"
                  value={imageForm.captions[language]}
                  onChange={(event) =>
                    setImageForm((current) => ({
                      ...current,
                      captions: {
                        ...current.captions,
                        [language]: event.target.value,
                      },
                    }))
                  }
                />
              </label>
            ))}

            <fieldset className="grid gap-1 text-sm sm:col-span-2 lg:col-span-3">
              <legend>Afficher la nouvelle image pour</legend>
              <div className="flex flex-wrap gap-3">
                {IMAGE_LANGUAGES.map((language) => (
                  <label key={language}>
                    <input
                      type="checkbox"
                      className="mr-1"
                      aria-label={`Nouvelle image en ${language.toUpperCase()}`}
                      checked={imageForm.activeLanguages.includes(language)}
                      onChange={(event) =>
                        setImageForm((current) => ({
                          ...current,
                          activeLanguages: event.target.checked
                            ? [...new Set([...current.activeLanguages, language])]
                            : current.activeLanguages.filter(
                                (item) => item !== language
                              ),
                        }))
                      }
                    />
                    {language.toUpperCase()}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="grid gap-1 text-sm">
              <span>Alignement</span>
              <select
                aria-label="Alignement de l’image"
                className="rounded border border-gray-300 px-2 py-1"
                value={imageForm.align}
                onChange={(event) =>
                  updateImageAlignment(event.target.value)
                }
              >
                <option value="left">Gauche</option>
                <option value="center">Centre</option>
                <option value="right">Droite</option>
                <option value="full">Pleine largeur</option>
              </select>
            </label>

            <label className="grid gap-1 text-sm">
              <span>Largeur</span>
              <input
                aria-label="Largeur de l’image"
                className="rounded border border-gray-300 px-2 py-1"
                type="number"
                min={imageForm.widthUnit === 'px' ? 80 : 10}
                max={imageForm.widthUnit === 'px' ? 2000 : 100}
                step={imageForm.widthUnit === 'px' ? 1 : 0.1}
                value={imageForm.widthValue}
                disabled={imageForm.align === 'full'}
                onChange={(event) =>
                  setImageForm((current) => ({
                    ...current,
                    widthValue: event.target.value,
                  }))
                }
              />
            </label>

            <label className="grid gap-1 text-sm">
              <span>Unité</span>
              <select
                aria-label="Unité de largeur de l’image"
                className="rounded border border-gray-300 px-2 py-1"
                value={imageForm.widthUnit}
                disabled={imageForm.align === 'full'}
                onChange={(event) =>
                  setImageForm((current) => ({
                    ...current,
                    widthUnit: event.target.value,
                    widthValue:
                      event.target.value === 'px' ? 320 : 60,
                  }))
                }
              >
                <option value="percent">Pourcentage (%)</option>
                <option value="px">Pixels (px)</option>
              </select>
            </label>

            <label className="grid gap-1 text-sm">
              <span>Habillage</span>
              <select
                aria-label="Habillage du texte"
                className="rounded border border-gray-300 px-2 py-1"
                value={imageForm.wrap}
                disabled={['center', 'full'].includes(imageForm.align)}
                onChange={(event) =>
                  setImageForm((current) => ({
                    ...current,
                    wrap: event.target.value,
                  }))
                }
              >
                <option value="none">Aucun</option>
                <option value="around">Texte autour</option>
              </select>
            </label>
          </div>

          {imageError && (
            <p className="text-sm text-red-700" role="alert">
              {imageError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm"
              disabled={uploadingImage}
              onClick={() => {
                setShowImagePanel(false);
                setImageError('');
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-70"
              disabled={uploadingImage}
              onClick={
                existingImageIndex === ''
                  ? insertUploadedImage
                  : insertExistingImage
              }
            >
              {uploadingImage ? (
                <span
                  className="inline-flex items-center gap-2"
                  role="status"
                  aria-live="polite"
                >
                  <LoaderCircle className="animate-spin" size={16} aria-hidden="true" />
                  Chargement de l’image {imageUploadProgress.current} sur{' '}
                  {imageUploadProgress.total}…
                </span>
              )
                : existingImageIndex === ''
                  ? imageFiles.length > 1
                    ? 'Insérer les images'
                    : 'Insérer l’image'
                  : 'Insérer l’image existante'}
            </button>
          </div>
        </div>
      )}

      {showTablePanel && (
        <div className="book-table-placement-panel">
          <p>Le tableau sera placé à la position mémorisée du curseur.</p>
          <label>
            <span>Tableau</span>
            <select
              aria-label="Tableau à placer"
              value={selectedTableId}
              onChange={(event) => setSelectedTableId(event.target.value)}
            >
              {stableTables.map((table, index) => (
                <option value={table.id} key={table.id}>
                  {table.title || table.caption || `Tableau ${index + 1}`}
                </option>
              ))}
            </select>
          </label>
          <div className="book-table-placement-actions">
            <button type="button" onClick={cancelTablePlacement}>
              Annuler
            </button>
            <button type="button" onClick={placeSelectedTable}>
              Placer le tableau
            </button>
          </div>
        </div>
      )}

      {/* Barre d'outils flottante (contextuelle) */}
      {shouldShowToolbar && (
        <div
          ref={toolbarRef}
          className="bg-white border border-gray-300 rounded-lg shadow-xl p-2 flex items-center gap-1"
          style={getToolbarPosition()}
        >
          {/* Gras */}
          <button
            onClick={() => handleToolbarAction('bold')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('bold') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Gras"
          >
            <Bold size={16} />
          </button>

          {/* Italique */}
          <button
            onClick={() => handleToolbarAction('italic')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('italic') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Italique"
          >
            <Italic size={16} />
          </button>

          {/* Souligné */}
          <button
            onClick={() => handleToolbarAction('underline')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('underline') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Souligné"
          >
            <UnderlineIcon size={16} />
          </button>

          <button
            type="button"
            onClick={() => handleToolbarAction('subscript')}
            className={`rounded p-2 text-sm font-semibold hover:bg-gray-100 ${
              isActive('subscript') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            aria-label="Indice X₂ de la sélection"
            aria-pressed={isActive('subscript')}
            title="Indice (texte en bas)"
          >
            X<sub>2</sub>
          </button>

          <button
            type="button"
            onClick={() => handleToolbarAction('superscript')}
            className={`rounded p-2 text-sm font-semibold hover:bg-gray-100 ${
              isActive('superscript') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            aria-label="Exposant X² de la sélection"
            aria-pressed={isActive('superscript')}
            title="Exposant (texte en haut)"
          >
            X<sup>2</sup>
          </button>

          {/* Séparateur */}
          <div className="w-px h-6 bg-gray-300 mx-1" />

          <button
            type="button"
            onClick={openLinkPanel}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('link') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            aria-label="Lien de la sélection"
            title="Ajouter ou modifier un lien"
          >
            <LinkIcon size={16} />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Listes */}
          <button
            onClick={() => handleToolbarAction('bulletList')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('bulletList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Liste à puces"
          >
            <List size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('orderedList')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('orderedList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Liste numérotée"
          >
            <ListOrdered size={16} />
          </button>

          {/* Alignement */}
          <button
            onClick={() => handleToolbarAction('align', 'left')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignLeft') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Aligner à gauche"
          >
            <AlignLeft size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('align', 'center')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignCenter') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Centrer"
          >
            <AlignCenter size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('align', 'right')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignRight') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Aligner à droite"
          >
            <AlignRight size={16} />
          </button>

          <button
            onClick={() => handleToolbarAction('align', 'justify')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isActive('alignJustify') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
            }`}
            title="Justifier"
          >
            <AlignJustify size={16} />
          </button>

          {/* Séparateur */}
          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Couleur */}
          <div className="relative">
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
              title="Couleur du texte"
            >
              <Palette size={16} />
            </button>
            
            {showColorPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 grid grid-cols-4 gap-1 z-50">
                {colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleToolbarAction('color', color)}
                    className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Police */}
          <div className="relative">
            <button
              onClick={() => setShowFontPicker(!showFontPicker)}
              className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600 flex items-center gap-1"
              title="Police"
            >
              <Type size={16} />
              <ChevronDown size={12} />
            </button>
            
            {showFontPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 min-w-[150px] z-50">
                {fonts.map((font) => (
                  <button
                    key={font.value}
                    onClick={() => handleToolbarAction('font', font.value)}
                    className="block w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Taille de police */}
          <div className="relative">
            <button
              onClick={() => setShowFontSizePicker(!showFontSizePicker)}
              className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600 flex items-center gap-1"
              title="Taille de police"
            >
              <span className="text-xs font-semibold">{getCurrentFontSize() || 16}px</span>
              <ChevronDown size={12} />
            </button>

            {showFontSizePicker && (
              <div data-font-size-picker className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 min-w-[120px] z-50">
                {fontSizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => handleToolbarAction('fontSize', size)}
                    className="block w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                    style={{ fontSize: `${size}px` }}
                  >
                    {size}px
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Surface d'édition */}
      <div
        className="relative min-w-0"
        data-testid="rich-text-editor-surface"
      >
        <div
          className="book-list-markers-visible prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
          onClick={openClickedLink}
        >
          <EditorContent
            editor={editor}
            className={`min-h-[100px] p-3 border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all ${
              !editable ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
            }`}
          />
        </div>

        {/* Placeholder personnalisé */}
        {editor.isEmpty && editable && (
          <div className="pointer-events-none absolute left-3 top-3 text-gray-400">
            {placeholder}
          </div>
        )}
      </div>

      {showImageLibrary && onImagesChange && (
        <div className="mt-3">
          <VisualImageListEditor
            images={images}
            contentLanguage={contentLanguage}
            onChange={changeVisualImages}
            onPlaceImage={openLibraryImagePanel}
          />
        </div>
      )}

      {editingTable && (
        <section
          className="book-table-inline-editor"
          role="region"
          aria-label={`Modification du tableau ${
            editingTable.title || editingTable.caption || editingTable.id
          }`}
        >
          <div className="book-table-inline-editor__header">
            <strong>
              Modifier : {editingTable.title || editingTable.caption || 'Tableau'}
            </strong>
            <button
              type="button"
              aria-label="Fermer l’éditeur du tableau"
              onClick={() => setEditingTableId('')}
            >
              Fermer
            </button>
          </div>
          <TableEditor table={editingTable} onChange={updateEditingTable} />
        </section>
      )}
    </div>
  );
};

export default RichTextEditor;
